import { ChangeDetectionStrategy, Component, forwardRef, input, signal, type InputSignal, type WritableSignal } from '@angular/core';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { type ICountryPhoneMetaDto } from '@pecan/api';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { CountryOptionComponent } from '../country-option/country-option.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatSelect, MatSelectTrigger, MatOption, CountryOptionComponent],
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneCodeSelectComponent),
        },
    ],
    selector: 'app-phone-code-select',
    templateUrl: './phone-code-select.component.html',
})
export class PhoneCodeSelectComponent implements ControlValueAccessor {
    public readonly countryOptions: InputSignal<ICountryPhoneMetaDto[] | null> = input.required<ICountryPhoneMetaDto[] | null>();

    public readonly disabled: WritableSignal<boolean> = signal<boolean>(false);

    public readonly selectedOption: WritableSignal<ICountryPhoneMetaDto | null> = signal<ICountryPhoneMetaDto | null>(null);

    public writeValue(value: ICountryPhoneMetaDto | null): void {
        this.selectedOption.set(value);
    }

    public registerOnChange(fn: (value: ICountryPhoneMetaDto | null) => void): void {
        this._onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this._onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled.set(isDisabled);
    }

    public onSelectionChange(country: ICountryPhoneMetaDto): void {
        this.selectedOption.set(country);

        this._onChange(country);
        this._onTouched();
    }

    public trackByCountry(_index: number, country: ICountryPhoneMetaDto): string {
        return country.isoCode;
    }

    public compareCountries(country1: ICountryPhoneMetaDto, country2: ICountryPhoneMetaDto): boolean {
        return country1 && country2 ? country1.isoCode === country2.isoCode : country1 === country2;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private _onChange: (value: ICountryPhoneMetaDto | null) => void = () => {};

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private _onTouched: () => void = () => {};
}
